import { useCallback } from 'react';
import { type Route, type RouteSegments, useNavigation } from 'expo-router';
import { Platform } from 'react-native';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns a type-safe `reset` navigation function for the Expo Router setup.
 */
export const useSafeExpoNavigationReset = () => {
  try {
    const navigation = useNavigation('/');

    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useCallback(
      (state: ResetState) => {
        if (Platform.OS === 'web') {
          navigation.reset(state as never);
          return;
        }

        // NOTE: We use idle callback on native to prevent conflicts between
        //       multiple modals.
        requestIdleCallback(() => {
          navigation.reset(state as never);
        });
      },
      [navigation],
    );
  } catch {
    return null;
  }
};

// ─── Types ───────────────────────────────────────────────────────────────────

type ResetRoute = {
  name: RootLevelRouteName;
  state?: {
    routes: {
      name: ResetRouteName;
      params?: Record<string, unknown>;
      state?: { routes: { name: ResetRouteName; params?: Record<string, unknown> }[] }; // prettier-ignore
    }[];
  };
  params?: Record<string, string>;
};

type RootLevelRouteName = RouteSegments<Route>[number];
type ResetRouteName = RootLevelRouteName | NormalizedRoute;

type ResetState = {
  index?: number;
  routes: ResetRoute[];
};

type NormalizedRoute = Route extends `/${infer R}` ? R | `${R}/index` : never;
